import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { Phone, Sparkles, Gift, ArrowRight, Clock, MapPin, Mail, Star } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { QuoteForm } from "@/components/QuoteForm";
import {
  BRAND,
  IMAGES,
  RESIDENTIAL_SERVICES,
  COMMERCIAL_SERVICES,
  WHY_CHOOSE,
} from "@/data/site";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1, ease: "easeOut" },
  }),
};

const Overline = ({ children }) => (
  <span className="text-sm uppercase tracking-[0.2em] font-bold text-cyan-600">{children}</span>
);

const ServiceCard = ({ item, i, testid }) => {
  const Icon = Icons[item.icon] || Icons.Droplets;
  return (
    <motion.div
      variants={fadeUp}
      custom={i}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: "-60px" }}
      data-testid={testid}
      className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm transition-all hover:-translate-y-1 hover:shadow-md"
    >
      <div className="w-14 h-14 rounded-full bg-cyan-50 flex items-center justify-center text-cyan-600 mb-6">
        <Icon className="h-7 w-7" />
      </div>
      <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">{item.title}</h3>
      <p className="text-slate-600 leading-relaxed">{item.desc}</p>
    </motion.div>
  );
};

export default function Home() {
  return (
    <div className="bg-white overflow-x-hidden">
      {/* Offer banner */}
      <div
        data-testid="offer-banner"
        className="fixed top-0 left-0 w-full z-[60] bg-lime-500 text-slate-900 py-2 px-4 text-center text-xs sm:text-sm font-bold flex items-center justify-center gap-2"
      >
        <Gift className="h-4 w-4 flex-shrink-0" />
        <span>Limited Time Offer: 50% OFF your 1st service + a FREE Garbage Can Cleanup!</span>
      </div>

      <Navbar />

      {/* Hero */}
      <section id="hero" className="relative pt-40 pb-20 md:pt-44 md:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div initial="hidden" animate="show" variants={fadeUp}>
              <div className="inline-flex items-center gap-2 rounded-full bg-cyan-50 border border-cyan-100 px-4 py-1.5 mb-6">
                <Sparkles className="h-4 w-4 text-cyan-600" />
                <span className="text-sm font-semibold text-cyan-700">Pressure Cleaning Specialists</span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
                Making South Florida <span className="text-cyan-500">Shine!</span>
              </h1>
              <p className="mt-6 text-base lg:text-lg text-slate-600 leading-relaxed max-w-xl">
                Professional Residential &amp; Commercial Pressure Cleaning Services You Can Trust.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a
                  href="#contact"
                  data-testid="hero-primary-cta"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-cyan-500 px-8 py-4 text-base font-bold text-white transition-all hover:bg-cyan-600 hover:shadow-lg hover:scale-105"
                >
                  Get a Free Quote Today <ArrowRight className="h-5 w-5" />
                </a>
                <a
                  href={BRAND.phoneHref}
                  data-testid="hero-secondary-cta"
                  className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-slate-900 px-8 py-4 text-base font-bold text-slate-900 transition-all hover:bg-slate-900 hover:text-white"
                >
                  <Phone className="h-5 w-5" /> Call Now: {BRAND.phone}
                </a>
              </div>
              <div className="mt-10 flex items-center gap-6">
                <div className="flex items-center gap-1 text-yellow-400">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-current" />
                  ))}
                </div>
                <span className="text-sm font-medium text-slate-500">Trusted across Broward County</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="relative"
            >
              <div className="relative rounded-[2rem] overflow-hidden shadow-2xl aspect-[4/5] max-w-md mx-auto lg:mx-0 ml-auto">
                <img
                  src={IMAGES.heroAction}
                  alt="Pressure washing in action"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-2 sm:left-6 bg-white rounded-2xl shadow-xl p-5 flex items-center gap-4 border border-slate-100">
                <div className="w-12 h-12 rounded-full bg-lime-100 flex items-center justify-center text-lime-600">
                  <Icons.ShieldCheck className="h-6 w-6" />
                </div>
                <div>
                  <p className="font-heading font-bold text-slate-900 leading-tight">100% Satisfaction</p>
                  <p className="text-sm text-slate-500">Guaranteed Results</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Welcome / Value prop */}
      <section id="welcome" className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
            <Overline>Welcome to {BRAND.legal}</Overline>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 leading-tight">
              We bring the shine back to your property.
            </h2>
            <p className="mt-6 text-base lg:text-lg text-slate-600 leading-relaxed">
              Serving Broward County and the greater South Florida area, our specialized pressure cleaning
              techniques remove years of stubborn dirt, grime, mold, and mildew. Whether it's your family home
              or a high-traffic commercial facility, we deliver pristine results with unmatched reliability.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why choose us */}
      <section id="why" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <Overline>Why Choose Us</Overline>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">
              Why Choose Luigi's Hydro Clean?
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {WHY_CHOOSE.map((item, i) => (
              <ServiceCard key={item.title} item={item} i={i} testid={`why-card-${i}`} />
            ))}
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="py-20 md:py-28 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
              <span className="text-sm uppercase tracking-[0.2em] font-bold text-cyan-400">About Us</span>
              <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
                Pristine Clean, Every Single Time
              </h2>
              <p className="mt-6 text-slate-300 leading-relaxed">
                Founded with a mission to deliver top-tier pressure washing solutions across South Florida, we
                pride ourselves on exceptional workmanship and outstanding customer service.
              </p>
              <p className="mt-4 text-slate-300 leading-relaxed">
                We understand that South Florida's climate can take a toll on exterior surfaces. Humidity, heavy
                rain, and intense sun lead to rapid buildup of mold, algae, and grime. Our team is equipped with
                high-performance, professional-grade pressure washing and soft-washing systems designed to safely
                deep-clean any surface without causing damage.
              </p>
              <p className="mt-4 text-slate-300 leading-relaxed">
                From restoring the curb appeal of your neighborhood driveway to ensuring your commercial property
                presents a spotless image, we handle jobs of all sizes with precision and care.
              </p>
              <a
                href="#contact"
                data-testid="about-cta"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-cyan-500 px-8 py-4 text-base font-bold text-white transition-all hover:bg-cyan-600 hover:shadow-lg"
              >
                Request Your Free Estimate <ArrowRight className="h-5 w-5" />
              </a>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="rounded-[2rem] overflow-hidden shadow-2xl aspect-[4/3]">
                <img src={IMAGES.aboutWorker} alt="Technician pressure washing" className="w-full h-full object-cover" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Residential Services */}
      <section id="residential" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-10 lg:gap-14 items-start">
            <div className="lg:sticky lg:top-28">
              <Overline>Residential Services</Overline>
              <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                Bring back your home's curb appeal
              </h2>
              <p className="mt-5 text-slate-600 leading-relaxed">
                Safe, effective cleaning for every surface around your home — from driveways to siding.
              </p>
              <div className="mt-8 rounded-2xl overflow-hidden shadow-lg aspect-[4/3]">
                <img src={IMAGES.driveway} alt="Clean residential driveway" className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-6">
              {RESIDENTIAL_SERVICES.map((item, i) => (
                <ServiceCard key={item.title} item={item} i={i} testid={`residential-card-${i}`} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Commercial Services */}
      <section id="commercial" className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-6 order-2 lg:order-1">
              {COMMERCIAL_SERVICES.map((item, i) => (
                <ServiceCard key={item.title} item={item} i={i} testid={`commercial-card-${i}`} />
              ))}
            </div>
            <div className="lg:sticky lg:top-28 order-1 lg:order-2">
              <Overline>Commercial Services</Overline>
              <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                A spotless image builds trust
              </h2>
              <p className="mt-5 text-slate-600 leading-relaxed">
                Complete pressure washing solutions for storefronts, HOAs, plazas, and industrial zones.
              </p>
              <div className="mt-8 rounded-2xl overflow-hidden shadow-lg aspect-[4/3]">
                <img src={IMAGES.commercial} alt="Commercial building exterior" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <Overline>Contact Us</Overline>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">
              Ready to See Your Property Shine?
            </h2>
            <p className="mt-5 text-base lg:text-lg text-slate-600">
              Get in touch today for a fast, free, no-obligation estimate.
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-2 space-y-6">
              {[
                { icon: Phone, label: "Phone", value: BRAND.phone, href: BRAND.phoneHref },
                { icon: Mail, label: "Email", value: BRAND.email, href: `mailto:${BRAND.email}` },
                { icon: MapPin, label: "Service Area", value: BRAND.area },
                { icon: Clock, label: "Business Hours", value: BRAND.hours },
              ].map((c) => (
                <div
                  key={c.label}
                  data-testid={`contact-info-${c.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="flex items-start gap-4 rounded-2xl bg-slate-50 border border-slate-100 p-5"
                >
                  <div className="w-12 h-12 rounded-full bg-cyan-500 flex items-center justify-center text-white flex-shrink-0">
                    <c.icon className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-sm font-bold uppercase tracking-wide text-slate-400">{c.label}</p>
                    {c.href ? (
                      <a href={c.href} className="text-lg font-semibold text-slate-900 hover:text-cyan-600 transition-colors">
                        {c.value}
                      </a>
                    ) : (
                      <p className="text-lg font-semibold text-slate-900">{c.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="lg:col-span-3">
              <QuoteForm />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const Footer = () => {
  const links = [
    { label: "Home", href: "#hero" },
    { label: "About", href: "#about" },
    { label: "Residential Services", href: "#residential" },
    { label: "Commercial Services", href: "#commercial" },
    { label: "Contact Us", href: "#contact" },
  ];
  const socials = [
    { icon: Icons.Facebook, href: "#", label: "facebook" },
    { icon: Icons.Instagram, href: "#", label: "instagram" },
    { icon: Icons.Twitter, href: "#", label: "twitter" },
  ];
  return (
    <footer data-testid="site-footer" className="bg-slate-900 text-slate-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-3">
              <img src={BRAND.logo} alt={BRAND.name} className="h-16 w-16 object-contain" />
              <span className="font-heading font-extrabold text-lg text-white leading-none">
                Luigi's<br /><span className="text-cyan-400">Hydro Clean</span>
              </span>
            </div>
            <p className="mt-5 font-heading text-xl font-bold text-cyan-400">{BRAND.tagline}</p>
            <div className="mt-5 flex gap-3">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  data-testid={`social-${s.label}`}
                  className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-slate-300 hover:bg-cyan-500 hover:text-white transition-colors"
                  aria-label={s.label}
                >
                  <s.icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-heading font-bold text-white mb-4">Quick Links</h4>
            <ul className="space-y-3">
              {links.map((l) => (
                <li key={l.label}>
                  <a
                    href={l.href}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                    className="hover:text-cyan-400 transition-colors"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-bold text-white mb-4">Get In Touch</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-2"><Phone className="h-4 w-4 text-cyan-400" /> {BRAND.phone}</li>
              <li className="flex items-center gap-2"><Mail className="h-4 w-4 text-cyan-400" /> {BRAND.email}</li>
              <li className="flex items-start gap-2"><MapPin className="h-4 w-4 text-cyan-400 mt-0.5" /> {BRAND.area}</li>
              <li className="flex items-start gap-2"><Clock className="h-4 w-4 text-cyan-400 mt-0.5" /> Mon–Sat: 8AM–6PM · Sun: Closed</li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-400">
          <p>© 2026 {BRAND.legal}. All Rights Reserved.</p>
          <p>
            Designed &amp; Developed by{" "}
            <a
              href="https://www.letsprint.ai"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="letsprint-link"
              className="text-cyan-400 font-semibold hover:underline"
            >
              Let's Print
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};
